// category names are passed in by various different methods in different places
// in the app and this function will parse however they appear to give you the
// name.
export function determineCategoryName(item) {
  let name = "";
  if (item.category_name && (typeof item.category_name === "string" || item.category_name instanceof String)) {
    name = item.category_name;
  } else if (item.product_category && (typeof item.product_category === "string" || item.product_category instanceof String)) {
    name = item.product_category;
  } else if (item.category && item.category.name) {
    name = item.category.name;
  } else if (item.product_category && item.product_category.name) {
    name = item.product_category.name;
  }
  return name;
}

export function getPackageCaseOptions(item) {
  // this section determines whether the quantity is a case or a package
  // if its a case or package, show the unit (and or package if case) breakdown beneath it
  if (item.units_per_package || item.units_per_case) {
    const categoryName = determineCategoryName(item);

    let display = "";
    if (item.units_per_package) {
      display = `${display}${item.units_per_package} ${categoryName != "Live Plant" ? "units/pack" : "plants/tray"}`;
    }
    if (item.units_per_case) {
      display = `${display}, ${item.units_per_case} ${categoryName != "Live Plant" ? "packs/case" : "trays/flat"}`;
    }
    if (display) {
      return display;
    }
  }
  return "";
}

export function getFullUnitBreakdown(item) {
  // this section determines whether the quantity is a case or a package
  // if its a case or package, show the unit (and or package if case) breakdown beneath it
  if (item.units_per_package || item.units_per_case) {
    const categoryName = determineCategoryName(item);

    let display = "";
    if (item.units_per_package) {
      display = `${display}${item.units_per_package} ${categoryName != "Live Plant" ? "units per pack" : "plants per tray"}`;
    }
    if (item.units_per_case) {
      display = `${display}, ${item.units_per_case} ${categoryName != "Live Plant" ? "packs per case" : "trays per flat"}`;
    }
    if (item.units_per_case) {
      display = `${display}, ${item.units_per_package * item.units_per_case} total ${
        categoryName != "Live Plant" ? "units per case" : "plants per flat"
      }`;
    }
    if (display) {
      return display;
    }
  }
  return "";
}

export function getUnitMeasurementPriceBreakdown(product, batch) {
  let productUnitMeasurement = product.product_unit_of_measurement;
  let unitOfMeasurement = product.product_unit_size_unit_of_measurement;

  const gramPerPreroll = product.grams_per_preroll ? product.grams_per_preroll : product.gram_per_preroll;
  if (!unitOfMeasurement && product.unit_size_unit_measurement) {
    unitOfMeasurement = product.unit_size_unit_measurement.name;
  }
  if (!productUnitMeasurement && product.unit_measurement) {
    productUnitMeasurement = product.unit_measurement.name;
  }

  if (!unitOfMeasurement && !product.product_unit_size_unit_measurement) {
    unitOfMeasurement = product.unit_measurement_name;
    productUnitMeasurement = product.unit_measurement_name;
  }

  const categoryName = determineCategoryName(product);

  const returnData = {
    // packagePrice: null,
    // singlesPrice: null,
    // unitSizePrice: null,
    // prerollGramsPrice: null,
    // flowerGramsPrice:null,
  };

  if (batch) {
    const price = batch.tiered_listing_price ? batch.tiered_listing_price : batch.listing_price;
    let totalUnitQuantity = 1;

    // price per pack, and price per unit.
    if (product.units_per_case) {
      const packagePrice = price / product.units_per_case;
      const packagePriceValue = (packagePrice / 100).toFixed(3);
      const firstSinglesPriceValue = (packagePrice / product.units_per_package / 100).toFixed(3);

      returnData.packagePrice = `$${parseFloat(packagePriceValue)}/${categoryName != "Live Plant" ? "pack" : "tray"}`;
      returnData.singlesPrice = `$${parseFloat(firstSinglesPriceValue)}/${categoryName != "Live Plant" ? "unit" : "plant"}`;
      totalUnitQuantity = product.units_per_package * product.units_per_case;
    } else if (product.units_per_package) {
      const singlesPriceValue = (price / product.units_per_package / 100).toFixed(3);
      returnData.singlesPrice = `$${parseFloat(singlesPriceValue)}/${categoryName != "Live Plant" ? "unit" : "plant"}`;
      totalUnitQuantity = product.units_per_package;
    }

    // categories that have a unit size measurement.
    // Cartridge, Edibles and Drink, Extract, Merchandise (for some reason), Tinctures, Topicals and Wellness
    // If the unit size meaurement isn't units, we should figure out what the price per
    // unit size measurement is.
    if (product.unit_size) {
      if (product.unit_size && unitOfMeasurement && unitOfMeasurement != "Unit" && unitOfMeasurement != "unit") {
        const unitSizePrice = (price / (product.unit_size * totalUnitQuantity) / 100).toFixed(3);
        returnData.unitSizePrice = `$${parseFloat(unitSizePrice)}/${unitOfMeasurement}`;
      }
    } else if (gramPerPreroll) {
      // Prerolls we should figure out price per gram
      const gramsPrice = (price / (gramPerPreroll * totalUnitQuantity) / 100).toFixed(3);

      returnData.prerollGramsPrice = `$${parseFloat(gramsPrice)}/Gram`;
    } else if (categoryName == "Flower" && productUnitMeasurement == "Pound") {
      const poundsPrice = (price / 454 / 100).toFixed(3);
      returnData.flowerGramsPrice = `$${parseFloat(poundsPrice)}/Gram`;
    }
  }

  return returnData;
}

export function countDecimals(value) {
  if (value % 1 != 0) {
    return value.toString().split(".")[1].length;
  }
  return 0;
}

export function getProductMinMaxPrice(product) {
  const prices = [];
  const range = {
    min: 0,
    max: null,
  };

  product.batches.forEach((batch) => prices.push(batch.listing_price));
  const min = Math.min(...prices) / 100;
  const max = Math.max(...prices) / 100;
  range.min = countDecimals(min) > 0 ? min.toFixed(2) : min;
  range.max = countDecimals(max) > 0 ? max.toFixed(2) : max;

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  if (range.min == range.max) {
    return formatter.format(range.min);
  }

  return `${formatter.format(range.min)} - ${formatter.format(range.max)}`;
}

export function isDiscountedProduct(product) {
  return product.batches.some((batch) => !!batch.tiered_listing_price);
}

export function getProductTierMinMaxPrice(product) {
  const prices = [];
  const range = {
    min: 0,
    max: null,
  };

  product.batches.forEach((batch) => prices.push(!batch.tiered_listing_price ? batch.listing_price : batch.tiered_listing_price));

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const min = Math.min(...prices) / 100;
  const max = Math.max(...prices) / 100;
  range.min = countDecimals(min) > 0 ? min.toFixed(2) : min;
  range.max = countDecimals(max) > 0 ? max.toFixed(2) : max;

  if (range.min == range.max) {
    return formatter.format(range.min);
  }

  return `${formatter.format(range.min)} - ${formatter.format(range.max)}`;
}

export function ptoGetMinPrice(product) {
  let min = 1000000;
  if (product.pto_oneg_listing_price) {
    if (product.pto_oneg_listing_price < min) {
      min = product.pto_oneg_listing_price;
    }
  }

  if (product.pto_twog_listing_price) {
    if (product.pto_twog_listing_price < min) {
      min = product.pto_twog_listing_price;
    }
  }

  if (product.pto_eighthoz_listing_price) {
    if (product.pto_eighthoz_listing_price < min) {
      min = product.pto_eighthoz_listing_price;
    }
  }

  if (product.pto_quarteroz_listing_price) {
    if (product.pto_quarteroz_listing_price < min) {
      min = product.pto_quarteroz_listing_price;
    }
  }
  if (product.pto_halfoz_listing_price) {
    if (product.pto_halfoz_listing_price < min) {
      min = product.pto_halfoz_listing_price;
    }
  }
  if (product.pto_oneoz_listing_price) {
    if (product.pto_oneoz_listing_price < min) {
      min = product.pto_oneoz_listing_price;
    }
  }
  if (product.pto_quarterpound_listing_price) {
    if (product.pto_quarterpound_listing_price < min) {
      min = product.pto_quarterpound_listing_price;
    }
  }
  if (product.pto_halfpound_listing_price) {
    if (product.pto_halfpound_listing_price < min) {
      min = product.pto_halfpound_listing_price;
    }
  }
  if (product.pto_onepound_listing_price) {
    if (product.pto_onepound_listing_price < min) {
      min = product.pto_onepound_listing_price;
    }
  }

  return min;
}

export function ptoGetMaxPrice(product) {
  let max = 0;

  if (product.pto_oneg_listing_price) {
    if (product.pto_oneg_listing_price > max) {
      max = product.pto_oneg_listing_price;
    }
  }
  if (product.pto_twog_listing_price) {
    if (product.pto_twog_listing_price > max) {
      max = product.pto_twog_listing_price;
    }
  }
  if (product.pto_eighthoz_listing_price) {
    if (product.pto_eighthoz_listing_price > max) {
      max = product.pto_eighthoz_listing_price;
    }
  }
  if (product.pto_quarteroz_listing_price) {
    if (product.pto_quarteroz_listing_price > max) {
      max = product.pto_quarteroz_listing_price;
    }
  }
  if (product.pto_halfoz_listing_price) {
    if (product.pto_halfoz_listing_price > max) {
      max = product.pto_halfoz_listing_price;
    }
  }
  if (product.pto_oneoz_listing_price) {
    if (product.pto_oneoz_listing_price > max) {
      max = product.pto_oneoz_listing_price;
    }
  }
  if (product.pto_quarterpound_listing_price) {
    if (product.pto_quarterpound_listing_price > max) {
      max = product.pto_quarterpound_listing_price;
    }
  }
  if (product.pto_halfpound_listing_price) {
    if (product.pto_halfpound_listing_price > max) {
      max = product.pto_halfpound_listing_price;
    }
  }
  if (product.pto_onepound_listing_price) {
    if (product.pto_onepound_listing_price > max) {
      max = product.pto_onepound_listing_price;
    }
  }

  return max;
}

export function pdpSizesAvailable(product) {
  let display = "";
  if (product.pto_oneg_listing_price) {
    display = `${`${display}1g`}, `;
  }
  if (product.pto_twog_listing_price) {
    display = `${`${display}2g`}, `;
  }
  if (product.pto_eighthoz_listing_price) {
    display = `${`${display}1/8oz`}, `;
  }
  if (product.pto_quarteroz_listing_price) {
    display = `${`${display}1/4oz`}, `;
  }
  if (product.pto_halfoz_listing_price) {
    display = `${`${display}1/2oz`}, `;
  }
  if (product.pto_oneoz_listing_price) {
    display = `${`${display}1oz`}, `;
  }
  if (product.pto_quarterpound_listing_price) {
    display = `${`${display}1/4lb`}, `;
  }
  if (product.pto_halfpound_listing_price) {
    display = `${`${display}1/2lb`}, `;
  }
  if (product.pto_onepound_listing_price) {
    display = `${`${display}1lb`}, `;
  }
  const lastComma = display.lastIndexOf(",");
  if (lastComma && display.length > 2) {
    display = display.substring(0, lastComma);
  }
  return display;
}

export function cultivarType(cultivarTypeValue) {
  const type = cultivarTypeValue || "none";
  let baseClasses = " d-inline-block px-2 py-0 text-white";
  if (type.match("Sativa")) {
    baseClasses += " bg-danger";
  } else if (type.match("Hybrid")) {
    baseClasses += " bg-info";
  } else if (type.match("Indica")) {
    baseClasses += " bg-callout";
  } else {
    baseClasses += " bg-primary";
  }
  return baseClasses;
}

export function cultivarTypeBgColor(cultivarTypeValue) {
  const type = cultivarTypeValue || "none";
  let baseClasses = "";
  if (type.match("Sativa")) {
    baseClasses += " bg-danger";
  } else if (type.match("Hybrid")) {
    baseClasses += " bg-info";
  } else if (type.match("Indica")) {
    baseClasses += " bg-callout";
  } else {
    baseClasses += " bg-primary";
  }
  return baseClasses;
}

export function calculateStorefrontListingQuantity(product) {
  let totalQuantity = 0;
  let someBatchHasInfiniteQuantities = false;

  if (product.batches) {
    product.batches.forEach((batch) => {
      totalQuantity += batch.quantity;
      if (batch.disable_inventory_tracking) {
        someBatchHasInfiniteQuantities = true;
      }
    });
  }
  if (someBatchHasInfiniteQuantities) {
    return "Unlimited";
  }
  return Number(totalQuantity.toFixed(7)).toString(); // toString is stripping off ending 0s that might be caused by toFixed
}

export function ___calculateStorefrontPotencyDisplay(product) {
  if (product.batches && product.batches.length) {
    let predCanMin = null;
    let predCanMax = 0;
    let predCanUnit = null;
    product.batches.forEach((batch) => {
      if (!predCanMin || batch.predominate_canabinoid_min_or_only < predCanMin) {
        predCanMin = batch.predominate_canabinoid_min_or_only;
      }
      if (batch.predominate_canabinoid_max > predCanMax && predCanUnit != "Varies") {
        predCanMax = batch.predominate_canabinoid_max;
      }
      if (batch.predominate_canabinoid_min_or_only > predCanMin && (!predCanMax || batch.predominate_canabinoid_min_or_only > predCanMax)) {
        predCanMax = batch.predominate_canabinoid_min_or_only;
      }
      if (!predCanUnit) {
        predCanUnit = batch.predominate_canabinoid_unit;
      } else if (predCanUnit && predCanUnit != batch.predominate_canabinoid_unit) {
        predCanUnit = "Variable";
        predCanMax = null;
        predCanMin = null;
      }
    });
    if (predCanMin) {
      return `${predCanMin}${predCanMax ? ` - ${predCanMax}` : ""}${predCanUnit || ""} ${product.predominateCanabinoidAbbreviation}`;
    }
    return null;
  }

  return null;
}

export function calculateStorefrontPotencyDisplay(product) {
  const potencies = [];
  let unit = null;

  // this runs through all batches and potentially but not likely the
  if (product.batches && product.batches.length) {
    product.batches.forEach((batch) => {
      if (!batch.hold) {
        if (batch.predominate_canabinoid_min_or_only && potencies.indexOf(batch.predominate_canabinoid_min_or_only) == -1) {
          potencies.push(batch.predominate_canabinoid_min_or_only);
        }
        if (batch.predominate_canabinoid_max && potencies.indexOf(batch.predominate_canabinoid_max) == -1) {
          potencies.push(batch.predominate_canabinoid_max);
        }

        if (batch.predominate_canabinoid_unit) {
          if (!unit) {
            unit = batch.predominate_canabinoid_unit;
          } else if (unit != batch.predominate_canabinoid_unit) {
            unit = "Variable";
          }
        }
      }
    });

    if (potencies.length == 0) {
      return null;
    }

    const min = Math.min(...potencies);
    const max = Math.max(...potencies);

    if (min == max) {
      return `${min}${unit} ${product.predominateCanabinoidAbbreviation}`;
    }

    return `${min}${unit} - ${max}${unit} ${product.predominateCanabinoidAbbreviation}`;
  }

  return null;
}

export function getUnit(item, single = false) {
  let singleDisplay = single;

  if (!singleDisplay && (item.quantity == 1 || item.order_quantity == 1)) {
    singleDisplay = true;
  }

  const categoryName = determineCategoryName(item);
  let unitOfMeasurementName = null;
  let unitOfMeasurementAlias = null;

  if (item.unit_measurement && item.unit_measurement.name) {
    unitOfMeasurementName = item.unit_measurement.name;
    unitOfMeasurementAlias = item.unit_measurement.alias;
  } else if (item.product_unit_of_measurement && item.product_unit_of_measurement.name) {
    unitOfMeasurementName = item.product_unit_of_measurement.name;
    unitOfMeasurementAlias = item.product_unit_of_measurement.alias;
  } else if (typeof item.product_unit_of_measurement === "string" || item.product_unit_of_measurement instanceof String) {
    unitOfMeasurementName = item.product_unit_of_measurement;
    unitOfMeasurementAlias = item.product_unit_of_measurementAlias;
  }

  if (item.units_per_case) {
    if (!singleDisplay) {
      const abbreviation = categoryName != "Live Plant" ? "Cs" : "Fs";
      const name = categoryName != "Live Plant" ? "Cases" : "Flats";
      return { name, abbreviation };
    }
    const abbreviation = categoryName != "Live Plant" ? "C" : "F";
    const name = categoryName != "Live Plant" ? "Case" : "Flat";
    return { name, abbreviation };
  }
  if (item.units_per_package) {
    if (!singleDisplay) {
      const abbreviation = categoryName != "Live Plant" ? "Ps" : "Ts";
      const name = categoryName != "Live Plant" ? "Packages" : "Trays";
      return { name, abbreviation };
    }
    const abbreviation = categoryName != "Live Plant" ? "P" : "T";
    const name = categoryName != "Live Plant" ? "Package" : "Tray";
    return { name, abbreviation };
  }
  // if its not a case or a package, just show the unit
  let abbreviation = categoryName != "Live Plant" ? unitOfMeasurementAlias : "Pls";
  let name = categoryName != "Live Plant" ? unitOfMeasurementName : "Pl";
  if (!singleDisplay) {
    abbreviation = `${abbreviation}s`;
    name = `${name}s`;
    return { name, abbreviation };
  }
  return { name, abbreviation };
}

export function getUnitSize(item) {
  // let categoryName = determineCategoryName(item);
  let unitOfMeasurement = item.product_unit_size_unit_of_measurement;

  // this is because some reports were feeding in bad data and this accomodated
  if (!unitOfMeasurement) {
    if (item.unit_size_unit_measurement) {
      unitOfMeasurement = item.unit_size_unit_measurement.name;
    }
  }

  let returnString = "";
  if (item.unit_size && unitOfMeasurement) {
    const size = Math.floor(item.unit_size * 100) / 100;
    const appliedUnitOfMeasurement = unitOfMeasurement || "";
    returnString = `${size} ${size > 1 ? `${appliedUnitOfMeasurement}s` : appliedUnitOfMeasurement} ${
      item.category_name != "Live Plant" ? "Per Unit" : "Per Plant"
    }`;
  }
  return returnString;
}
